import BottomBar from "@/components/core/actions/bottombar";
import { Topbar } from "@/components/layouts/topbar";
import clsx from "clsx";
import React, { useEffect, useMemo, useRef, useState } from "react";

interface ContentProps {
	children: React.ReactNode;
	className?: string;
	pageName?: string;
}

export function Content({ children, className, pageName }: ContentProps) {
	const hasBottomBar = React.Children.toArray(children).some((child) => React.isValidElement(child) && child.type === BottomBar);

	const [showFixed, setShowFixed] = useState(false);
	const lastScrollY = useRef(0);
	const barHeight = 100;

	/* detect if device is touch based */
	const isTouchDevice = useMemo(() => {
		if (typeof window === "undefined") return false;
		return "ontouchstart" in window || navigator.maxTouchPoints > 0 || window.matchMedia?.("(pointer: coarse)")?.matches;
	}, []);

	useEffect(() => {
		let timeoutId: number;

		const handleScroll = () => {
			const currentY = window.scrollY;

			/* show topbar when scrolling up */
			if (currentY <= barHeight) {
				setShowFixed(false);
			} else if (currentY < lastScrollY.current) {
				setShowFixed(true);
			} else {
				setShowFixed(false);
			}

			lastScrollY.current = currentY;
		};

		/* when mouse pointer move near treshold, topbar will show */
		const handleMouseMove = (e: MouseEvent) => {
			if (window.scrollY <= barHeight) {
				setShowFixed(false);
				return;
			}

			if (e.clientY < barHeight) {
				clearTimeout(timeoutId);
				setShowFixed(true);
			} else {
				clearTimeout(timeoutId);
				timeoutId = window.setTimeout(() => setShowFixed(false), 2000);
			}
		};

		window.addEventListener("scroll", handleScroll, { passive: true });

		if (!isTouchDevice) {
			window.addEventListener("mousemove", handleMouseMove);
		}

		return () => {
			window.removeEventListener("scroll", handleScroll);
			if (!isTouchDevice) {
				window.removeEventListener("mousemove", handleMouseMove);
			}
			clearTimeout(timeoutId);
		};
	}, [isTouchDevice]);

	return (
		<div className={clsx("flex flex-col w-full", hasBottomBar && "pb-20", className)}>
			{/* interactive topbar - do not remove */}
			<Topbar pageName={pageName} className={clsx("z-[3] fixed top-0 left-0 right-0 ", showFixed ? "translate-y-0" : "-translate-y-full")} />
			{/* static topbar - do not remove */}
			<Topbar pageName={pageName} />
			{/* main contents */}
			<div className="z-[1] flex flex-col items-center w-full h-full">{children}</div>
		</div>
	);
}
